import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  escalaService,
  type DadosCriarEscala,
  type DadosEditarEscala,
  type ItemParaAdicionar,
} from "@/modules/escala/services/escalaService";
// `EscalaResposta` é um alias manual de `database.types.ts` (mesmo padrão de
// `format.ts`) — `escalaService.ts` só o importa para uso interno, não o
// reexporta.
import type { EscalaResposta } from "@/shared/types/database.types";

const QUERY_KEY_ESCALAS = ["escalas"];
const detalheKey = (id: string) => ["escala", id];
const historicoKey = (id: string) => ["escala-historico", id];
const ULTIMA_EQUIPE_KEY = ["escala-ultima-equipe"];

/** Escala muda de data/situação (criar/editar/cancelar) — aparece/some/move no Calendário Central. */
function useInvalidarEscalasECalendario() {
  const queryClient = useQueryClient();
  return (escalaId?: string) => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_ESCALAS });
    if (escalaId) queryClient.invalidateQueries({ queryKey: detalheKey(escalaId) });
    queryClient.invalidateQueries({ queryKey: ["calendario"] });
    queryClient.invalidateQueries({ queryKey: ULTIMA_EQUIPE_KEY }); // "Repetir equipe" parte da escala mais recente
  };
}

/** Mutação de participação (adicionar/remover/alterar função/responder) não muda data/situação da escala — não precisa invalidar o Calendário. */
function useInvalidarParticipacao() {
  const queryClient = useQueryClient();
  return (escalaId: string) => {
    queryClient.invalidateQueries({ queryKey: detalheKey(escalaId) });
    queryClient.invalidateQueries({ queryKey: historicoKey(escalaId) });
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_ESCALAS }); // badge "sua participação" na lista
    queryClient.invalidateQueries({ queryKey: ULTIMA_EQUIPE_KEY });
  };
}

export function useEscalas(pessoaId: string | undefined) {
  return useQuery({
    queryKey: [...QUERY_KEY_ESCALAS, pessoaId ?? ""],
    queryFn: () => escalaService.listar(pessoaId as string),
    enabled: !!pessoaId,
  });
}

export function useEscalaDetalhe(escalaId: string | undefined) {
  return useQuery({
    queryKey: detalheKey(escalaId ?? ""),
    queryFn: () => escalaService.detalhe(escalaId as string),
    enabled: !!escalaId,
  });
}

export function usePodeVerHistorico(escalaId: string | undefined) {
  return useQuery({
    queryKey: ["escala-pode-ver-historico", escalaId ?? ""],
    queryFn: () => escalaService.podeVerHistorico(escalaId as string),
    enabled: !!escalaId,
  });
}

export function useEscalaHistorico(escalaId: string | undefined, habilitado: boolean) {
  return useQuery({
    queryKey: historicoKey(escalaId ?? ""),
    queryFn: () => escalaService.historico(escalaId as string),
    enabled: !!escalaId && habilitado,
  });
}

/** DEC-02: autoridade real (líder OU gestor do ministério dono) — nunca `gerencia_modulo`. */
export function usePodeGerirEscala(ministerioId: string | undefined) {
  return useQuery({
    queryKey: ["escala-pode-gerir", ministerioId ?? ""],
    queryFn: () => escalaService.podeGerir(ministerioId as string),
    enabled: !!ministerioId,
  });
}

export function useMinisteriosParaCriarEscala(pessoaId: string | undefined) {
  return useQuery({
    queryKey: ["escala-ministerios-para-criar", pessoaId ?? ""],
    queryFn: () => escalaService.ministeriosParaCriar(pessoaId as string),
    enabled: !!pessoaId,
  });
}

export function useFuncoesMinisterio(ministerioId: string | undefined) {
  return useQuery({
    queryKey: ["escala-funcoes-ministerio", ministerioId ?? ""],
    queryFn: () => escalaService.funcoesMinisterio(ministerioId as string),
    enabled: !!ministerioId,
  });
}

/**
 * Integrantes ativos do ministério que podem entrar na equipe. Com `escalaId`
 * (escala já criada) exclui quem já participa; sem ele (tela de nova escala)
 * traz todos os integrantes.
 */
export function useCandidatosParticipantes(ministerioId: string | undefined, escalaId?: string) {
  return useQuery({
    queryKey: ["escala-candidatos", ministerioId ?? "", escalaId ?? "nova"],
    queryFn: () => escalaService.candidatosParticipantes(ministerioId as string, escalaId),
    enabled: !!ministerioId,
  });
}

/** Nomes citados no histórico (melhor esforço — o que a RLS de `pessoas` não liberar fica sem nome). */
export function useNomesPessoas(pessoaIds: string[]) {
  const chave = [...new Set(pessoaIds)].sort();
  return useQuery({
    queryKey: ["escala-nomes-pessoas", chave],
    queryFn: () => escalaService.nomesPessoas(chave),
    enabled: chave.length > 0,
  });
}

/** Equipe da última escala do ministério — atalho "Repetir equipe" na criação. */
export function useUltimaEquipe(ministerioId: string | undefined) {
  return useQuery({
    queryKey: [...ULTIMA_EQUIPE_KEY, ministerioId ?? ""],
    queryFn: () => escalaService.ultimaEquipe(ministerioId as string),
    enabled: !!ministerioId,
  });
}

export function useCriarEscala() {
  const invalidar = useInvalidarEscalasECalendario();
  return useMutation({
    mutationFn: (dados: DadosCriarEscala) => escalaService.criar(dados),
    onSuccess: () => invalidar(),
  });
}

export function useEditarEscala() {
  const invalidar = useInvalidarEscalasECalendario();
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: DadosEditarEscala }) => escalaService.editar(id, dados),
    // onSettled (não só onSuccess): em conflito de versão (erro), o detalhe
    // precisa recarregar sozinho para trazer a versão real — nunca deixar a
    // tela com a versão antiga em cache (regra explícita da seção 8 do
    // relatório técnico: "refetch automático do detalhe, nunca sobrescrever
    // silenciosamente").
    onSettled: (_d, _e, v) => invalidar(v.id),
  });
}

export function useCancelarEscala() {
  const invalidar = useInvalidarEscalasECalendario();
  return useMutation({
    mutationFn: ({ id, versao, motivo }: { id: string; versao: number; motivo?: string | null }) =>
      escalaService.cancelar(id, versao, motivo),
    onSettled: (_d, _e, v) => invalidar(v.id),
  });
}

export function useAdicionarParticipantes() {
  const invalidar = useInvalidarParticipacao();
  return useMutation({
    mutationFn: ({ escalaId, itens }: { escalaId: string; itens: ItemParaAdicionar[] }) =>
      escalaService.adicionarParticipantes(escalaId, itens),
    onSuccess: (_d, v) => invalidar(v.escalaId),
  });
}

export function useRemoverParticipante() {
  const invalidar = useInvalidarParticipacao();
  return useMutation({
    mutationFn: (v: { escalaId: string; participacaoId: string }) =>
      escalaService.removerParticipante(v.participacaoId),
    onSuccess: (_d, v) => invalidar(v.escalaId),
  });
}

export function useAlterarFuncao() {
  const invalidar = useInvalidarParticipacao();
  return useMutation({
    mutationFn: (v: { escalaId: string; participacaoId: string; novaFuncaoId: string }) =>
      escalaService.alterarFuncao(v.participacaoId, v.novaFuncaoId),
    onSuccess: (_d, v) => invalidar(v.escalaId),
  });
}

export function useResponderEscala() {
  const invalidar = useInvalidarParticipacao();
  return useMutation({
    mutationFn: (v: {
      escalaId: string;
      participacaoId: string;
      resposta: Extract<EscalaResposta, "CONFIRMADA" | "RECUSADA">;
      justificativa?: string | null;
    }) => escalaService.responder(v.participacaoId, v.resposta, v.justificativa),
    onSuccess: (_d, v) => invalidar(v.escalaId),
  });
}

export function useRegistrarRespostaAdministrativa() {
  const invalidar = useInvalidarParticipacao();
  return useMutation({
    mutationFn: (v: {
      escalaId: string;
      participacaoId: string;
      resposta: Extract<EscalaResposta, "CONFIRMADA" | "RECUSADA">;
      justificativa?: string | null;
    }) => escalaService.registrarRespostaAdministrativa(v.participacaoId, v.resposta, v.justificativa),
    onSuccess: (_d, v) => invalidar(v.escalaId),
  });
}

/** Chamado antes de confirmar a inclusão em lote — nunca pessoa por pessoa (evita N+1). */
export function useVerificarConflito() {
  return useMutation({
    mutationFn: ({ escalaId, pessoaIds }: { escalaId: string; pessoaIds: string[] }) =>
      escalaService.verificarConflito(escalaId, pessoaIds),
  });
}
